import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import { getBookingDetails } from "../../../actions/admin/scheduled-event-actions";
import { Modal, ModalBody } from "reactstrap";
import UpcomingCancelModal from "../../admin/schedule/scheduled-events/upcoming-cancel-modal";
import UpcomingRescheduleModal from "../../admin/schedule/scheduled-events/upcoming-reschedule-modal";
import ConfirmModal from "../../../components/Modals/Confirm";
import { sendPostRequest } from "../../../actions/app-actions";
import { notification } from "../../../utils/utility";
import "../../admin/schedule/scheduled-events/scheduledEvents.scss";

// ─── DetailItem — reuses se-details__item from scheduledEvents.scss ───────────
const DetailItem = ({ label, value, wide = false }) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return (
    <div className={`se-details__item${wide ? " se-details__item--wide" : ""}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
};

const ViewDetailModal = (props) => {
  const { getBookingDetails, bookingId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);

  const [showCancelModal, setShowCancelModal]       = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal]     = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    setIsLoading(true);
    getBookingDetails({
      data: { id: bookingId },
      onSuccess: (response) => { setData(response); setIsLoading(false); },
      onError:   ()         => { setIsLoading(false); },
    });
  }, [bookingId]);

  const hideCancelModal     = () => setShowCancelModal(false);
  const hideRescheduleModal = () => setShowRescheduleModal(false);

  const onConfirm = (data) => {
    const { sendPostRequest, handleClose } = props;
    setIsLoading(true);
    sendPostRequest({
      url: `/booking/confirm`,
      data: { id: data.id },
      onSuccess: () => {
        setIsLoading(false);
        handleClose();
        notification.success("Booking successfully confirmed.");
      },
      onError: () => {
        setIsLoading(false);
        handleClose();
      },
    });
  };

  /* Build "When" label */
  const whenLabel = data
    ? [
        data.when,
        data.start_time && data.end_time ? `${data.start_time} – ${data.end_time}` : null,
        data.host_timezone ? `(${data.host_timezone})` : null,
      ].filter(Boolean).join(" ")
    : "";

  const canReschedule = props?.auth?.config?.staff_can_reschedule_booking === "E";
  const canCancel     = props?.auth?.config?.staff_can_cancel_booking === "E";
  const canConfirm    = props?.auth?.config?.staff_can_confirm_booking === "E";

  return (
    <>
      {showCancelModal && (
        <UpcomingCancelModal
          showCancelModal={showCancelModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideCancelModal}
          data={data}
          isBulk={false}
          isStaffCancellation={true}
          loadUpcomingEvents={props.loadBookings}
        />
      )}
      {showRescheduleModal && (
        <UpcomingRescheduleModal
          showRescheduleModal={showRescheduleModal}
          handleViewDetailClose={props.handleClose}
          handleClose={hideRescheduleModal}
          data={data}
          loadUpcomingEvents={props.loadBookings}
        />
      )}
      {showConfirmModal && (
        <ConfirmModal
          data={data}
          title="Confirm"
          message="Are you sure you want to confirm this booking?"
          onConfirm={onConfirm}
          onToggle={() => setShowConfirmModal(false)}
        />
      )}

      <Loader isShowLoader={isLoading} />
      <Modal
        returnFocusAfterClose
        isOpen={props.showViewDetailModal && !isLoading}
        toggle={props.handleClose}
        centered
        className="se-details-modal"
        contentClassName="se-details-modal__content"
      >
        <ModalBody>
          {data && (
            <div className="se-details">

              {/* ── Header ──────────────────────────────────────── */}
              <header className="se-details__header">
                <div className="se-details__header-copy">
                  <p className="se-details__eyebrow">Booking details</p>
                  <h2>{data.event_name}</h2>
                  {data.start_time && data.end_time && (
                    <span className="se-details__slot">
                      {data.start_time} – {data.end_time}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="se-details__close"
                  onClick={props.handleClose}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>

              {/* ── 2-column details grid ────────────────────────── */}
              <dl className="se-details__grid">
                <DetailItem label="When"              value={whenLabel}            wide />
                <DetailItem label="Host"              value={data.host} />
                <DetailItem label="Invitee"           value={data.invitee} />
                <DetailItem label="Duration"          value={data.event_duration} />
                <DetailItem label="Invitee time zone" value={data.invitee_timezone} />
                <DetailItem label="Venue"             value={data.venue} />
                <DetailItem label="Venue details"     value={data.venue_details} />
                <DetailItem label="Invitee instructions" value={data.customer_instruction} wide />
                <DetailItem label="Invitee feedback"     value={data.message_from_customer} wide />
                {(data.question_details || []).map((q, i) => (
                  <DetailItem key={i} label={q.question || "Question"} value={q.answer} wide />
                ))}
              </dl>

              {/* ── Action footer ────────────────────────────────── */}
              <div className="se-details__actions">
                {data.booking_status === "P" && canConfirm && data.staff_can_confirm_booking === "E" && (
                  <button
                    type="button"
                    className="se-btn se-btn--primary"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    Confirm
                  </button>
                )}
                {canReschedule && (
                  <button
                    type="button"
                    className="se-btn se-btn--ghost"
                    onClick={() => setShowRescheduleModal(true)}
                  >
                    Reschedule
                  </button>
                )}
                {data.booking_status !== "CC" && data.booking_status !== "CS" && canCancel && (
                  <button
                    type="button"
                    className="se-btn se-btn--danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel booking
                  </button>
                )}
              </div>

            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  getBookingDetails,
  sendPostRequest,
})(ViewDetailModal);
